<template>
  <div class="assignment-tile" :class="{ selected: assignment.selected }" @click="onTileClicked">
    <div class="assignment-tile-main">
      <header>
        <h3>
          {{ assignment.PlantName }} [{{ assignment.OwnsStatus }}]
          <a
            :href="`/oudirect/PlantHistory/${assignment.PlantID}`"
            target="_blank"
            class="plant-history-link"
            data-cy="plant-history-link"
          >
            <font-awesome-icon icon="history" aria-hidden="true" />
          </a>
        </h3>
      </header>

      <div class="description" style="font-weight: bold">
        {{ assignment.PlantCity }}, {{ assignment.PlantState }} {{ assignment.PlantCountry }} <br /><br />
        Visit Type: {{ assignment.VisitType }}
        <br />
        Visit Frequency: {{ assignment.VisitFrequencyWithASAP }}
        <br />
      </div>

      <div v-if="assignment.PlannedVisitDate" class="planned-visit-date-row">
        <span data-cy="planned-visit-date">
          Visit Planned Date: {{ new Date(assignment.PlannedVisitDate).toLocaleDateString() }}
        </span>
      </div>

      <div class="labelname" style="margin-top: 15px">
        <a
          v-if="myLastVisitLinkEnabled(assignment)"
          class="visit-link"
          :class="{ late: assignment.IsLate }"
          :href="`/oudirect/default?page=inspectionsRfrVisitInspectionForm&emailLink=YF&type=Inspection&mode=view&visitID=${getMyLastVisitId(
            assignment
          )}`"
        >
          RFR Last EIR: {{ getMyLastPlantVisit(assignment) }}</a
        >
        <div v-else>
          <span v-if="getMyLastPlantVisit(assignment)?.length > 0"
            >RFR Last EIR:
            {{ getMyLastPlantVisit(assignment) }}
          </span>
          <span v-else>RFR Last EIR: n/a</span>
        </div>
      </div>
      <div class="labelname">
        <a
          v-if="lastRfrVisitLinkEnabled(assignment)"
          class="visit-link"
          :href="`/oudirect/default?page=inspectionsRfrVisitInspectionForm&emailLink=YF&type=Inspection&mode=view&visitID=${getLastRfrVisitId(
            assignment
          )}`"
        >
          Plant Last EIR: {{ getLastRfrPlantVisit(assignment) }}</a
        >
        <div v-else>
          <span v-if="getLastRfrPlantVisit(assignment)?.length > 0"
            >Plant Last EIR:
            {{ getLastRfrPlantVisit(assignment) }}
          </span>
          <span v-else>Plant Last EIR: n/a</span>
        </div>
      </div>

      <div class="description">
        <div class="labelname">
          <br />
          YTD Ratio: {{ assignment.YtdVisitCounts }}
          <br />
          Assigned Ratio: {{ assignment.AssignedRatio }} Visits
        </div>
      </div>
      <!-- <span><button class="commentButton">Plant Comments</button> </span>
      <span><button class="commentButton">My Plant Comments</button></span> -->
    </div>

    <div class="assignment-tile-company">
      <span class="contact-name">Company Info: </span>
      <div class="description">
        <div class="industry">
          {{ assignment.CompanyName }}

          <div class="rc-email-action" @click="sendEmailClicked">
            <span class="contact-name">RC Info:</span>
            {{ assignment.RC }}
          </div>
          <div v-if="teamAssignments">
            <span class="contact-name">RFR Info:</span>
            {{ assignment.RfrLastFirstName }}
          </div>
        </div>
      </div>

      <div
        v-if="assignment.isGroupPlant && assignment.VisitTypeEnum == EnumVisitType.Regular"
        class="group-action"
        @click="detailsClicked"
      >
        Group Details
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { AssignmentWithSelection } from "@/api/rfrAssignments";
import { ref } from "vue";
import { EnumVisitType } from "@/Domain/EnumVisitType";
import {
  getLastRfrPlantVisit,
  getLastRfrVisitId,
  getMyLastPlantVisit,
  getMyLastVisitId,
  lastRfrVisitLinkEnabled,
  myLastVisitLinkEnabled,
} from "@/shared/helpers/assignmentsHelper";

const props = defineProps<{
  assignment: AssignmentWithSelection;
  teamAssignments: boolean;
}>();

const emit = defineEmits<{
  (e: "sendEmailClicked", assignment: AssignmentWithSelection): void;
  (e: "detailsClicked", assignment: AssignmentWithSelection): void;
}>();

const assignment = ref(props.assignment);

const sendEmailClicked = function () {
  emit("sendEmailClicked", props.assignment);
};

const detailsClicked = function () {
  emit("detailsClicked", props.assignment);
};

const onTileClicked = function (event: MouseEvent) {
  // if clicked on the email action or details action,
  // don't change the selection
  const className = (event.target as HTMLElement).className;
  if (
    className.includes &&
    (className.includes("rc-email-action") ||
      className.includes("group-action") ||
      className.includes("visit-link") ||
      className.includes("plant-history-link"))
  )
    return;
  assignment.value.selected = !assignment.value.selected;
};
</script>

<style scoped lang="scss">
.assignment-tile {
  margin-bottom: 20px;
  display: flex;
  box-shadow: 0 0 35px rgb(0 0 0 / 10%);
  border-radius: 12px;
  position: relative;

  &.selected:after {
    border: 2px solid #3a5da6;
    content: "";
    position: absolute;
    border-radius: 12px;
    width: 100%;
    height: 100%;
    animation: slideInFromTop 1s ease;
    transition: all 1s ease;
  }

  // &:not(.selected):after {
  //   border: 2px solid #3a5da6;
  //   content: "";
  //   position: absolute;
  //   border-radius: 12px;
  //   width: 100%;
  //   height: 0;
  //   // animation: slideInFromTop 1s ease reverse;
  //   transition: all 1s ease;
  // }

  @keyframes slideInFromTop {
    0% {
      height: 0;
      border-bottom: 0;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }
    90% {
      border-bottom: 0;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }
    95% {
      border-bottom: 1px;
      border-bottom-left-radius: 6px;
      border-bottom-right-radius: 6px;
    }
    100% {
      height: 100%;
      border-bottom: 2px;
      border-bottom-left-radius: 12px;
      border-bottom-right-radius: 12px;
    }
  }

  &-main {
    flex: 3;
    padding: 12px;

    @include media-breakpoint-up(sm) {
      padding: 20px;
    }

    > header {
      display: flex;
      align-items: center;
      margin-bottom: 10px;
    }
    h2 {
      font-size: 20px;
      font-weight: bold;
    }
    .location {
      color: #999;
      margin-left: 10px;
      margin-top: 3px;
      span:not(:last-child):after {
        content: ",";
      }
    }
    .description {
      display: flex;
    }
    .industry {
      font-style: italic;
    }
  }

  .planned-visit-date-row {
    margin-top: 12px;
  }

  &-company {
    flex: 1;
    background: #f7f7f7;
    border-left: 1px solid #efefef;
    min-width: 33%;
    max-width: 33%;
    word-break: break-all;
    padding: 12px;
    display: flex;
    flex-direction: column;

    @include media-breakpoint-up(sm) {
      padding: 20px;
    }

    .contact-name {
      //font-weight: bold;
      font-size: 12px;
      color: #666;
    }
    a,
    span {
      display: block;
      margin-bottom: 3px;
    }
  }

  .late {
    color: red;
    font-weight: bold;
  }

  .rc-email-action {
    color: #337ab7;
    cursor: pointer;
  }

  .group-action {
    color: #337ab7;
    cursor: pointer;
    margin-top: auto;
  }

  .visit-link,
  .plant-history-link {
    color: #337ab7;
    cursor: pointer;
    padding: 4px;

    &:hover {
      color: #23527c;
    }

    &.late {
      color: red;
      font-weight: bold;
    }
  }
}
</style>
